import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { NotificationService } from '../../../../core/services/NotificationService';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartService } from '../../../cart/core/services/CartService';
import {
  CardCodeInputModalComponent
} from '../card-code-input-modal/card-code-input-modal.component';
import {
  CardDetailsModalComponent
} from '../card-details-modal/card-details-modal.component';
import {
  CardAmountPickerModalComponent
} from '../card-amount-picker-modal/card-amount-picker-modal.component';
import { ICard } from '../../core/interfaces/ICard';
import { ICardType } from '../../core/interfaces/ICardType';
import { WebCashPermissionControlDirective } from '../../../auth/core/directives/web-cash-permission-control.directive';
import {
  VoucherDetailsModalComponent
} from '../../../vouchers/modals/voucher-details-modal/voucher-details-modal.component';
import { IVoucherDetails } from '../../../vouchers/core/interfaces/IVoucherDetails';
import { IVoucherTransaction } from '../../../vouchers/core/interfaces/IVoucherTransaction';
import {
  OnlineOrdersModalComponent
} from '../../../online-orders/modals/online-orders-modal/online-orders-modal.component';

@Component({
  selector: 'app-cinecard-actions-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    CardCodeInputModalComponent,
    CardDetailsModalComponent,
    CardAmountPickerModalComponent,
    WebCashPermissionControlDirective,
    VoucherDetailsModalComponent,
    OnlineOrdersModalComponent
  ],
  templateUrl: './cinecard-actions-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CinecardActionsModalComponent extends BaseModalComponent {

  @ViewChild('cardCodeInputModalComponent') cardCodeInputModalComponent: CardCodeInputModalComponent;
  @ViewChild('cardDetailsModalComponent') cardDetailsModalComponent: CardDetailsModalComponent;
  @ViewChild('cardAmountPickerModalComponent') cardAmountPickerModalComponent: CardAmountPickerModalComponent;
  @ViewChild('voucherDetailsModalComponent') voucherDetailsModalComponent: VoucherDetailsModalComponent;
  @ViewChild('onlineOrdersModalComponent') onlineOrdersModalComponent: OnlineOrdersModalComponent;

  private readonly _cartService = inject(CartService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  private _exchangeWithFee = false;
  private _exchangeOldCard: ICard = null;

  public fetchCardDetails(): void {
    this.close();
    this.cardCodeInputModalComponent.open(null, 'fetch-card-details');
  }

  public loadCardBalance(): void {
    this.close();
    this.cardCodeInputModalComponent.open(null, 'load-card-balance');
  }

  public withdrawCardBalance(): void {
    this.close();
    this.cardCodeInputModalComponent.open(null, 'withdraw-card-balance');
  }

  public onVoucherDetailsRequested(event: { voucherDetails: IVoucherDetails; transactions: IVoucherTransaction[] }): void {
    this.voucherDetailsModalComponent.open(event.voucherDetails, event.transactions);
  }

  public undoLastLoad(): void {
    this.close();
    this.cardCodeInputModalComponent.open(null, 'undo-last-load');
  }

  public cancelCustomerCard(): void {
    this.close();
    this.cardCodeInputModalComponent.open(null, 'cancel-customer-card');
  }

  public openOnlineOrders(): void {
    this.close();
    this.onlineOrdersModalComponent.open();
  }

  public exchangeCardWithFee(): void {
    this.startCardExchange(true);
  }

  public exchangeCardWithoutFee(): void {
    this.startCardExchange(false);
  }

  public onCodeSubmitted(event: {
    cardType?: ICardType;
    card?: ICard;
    cardActionType?: string;
    code?: string;
  }): void {
    if (event.cardActionType === 'fetch-card-details') {
      this.cardDetailsModalComponent.open({ card: event.card });
      return;
    }

    if (event.cardActionType === 'load-card-balance') {
      this.cardAmountPickerModalComponent.open(event.cardType, event.card);
      return;
    }

    if (event.cardActionType === 'withdraw-card-balance') {
      this.cardAmountPickerModalComponent.open(event.cardType, event.card, null, true);
      return;
    }

    if (event.cardActionType === 'exchange-card-old') {
      this._exchangeOldCard = event.card;
      this.cardCodeInputModalComponent.exchangeWithFee = this._exchangeWithFee;
      this.cardCodeInputModalComponent.exchangeOldCard = event.card;
      this.cardCodeInputModalComponent.open(null, 'exchange-card-new');
      return;
    }

    if (event.cardActionType === 'exchange-card-new') {
      this.submitCardExchange(event.card);
    }
  }

  private startCardExchange(withFee: boolean): void {
    this._exchangeWithFee = withFee;
    this._exchangeOldCard = null;
    this.close();
    this.cardCodeInputModalComponent.exchangeWithFee = withFee;
    this.cardCodeInputModalComponent.exchangeOldCard = null;
    this.cardCodeInputModalComponent.open(null, 'exchange-card-old');
  }

  private submitCardExchange(newCard: ICard): void {
    this._cartService.exchangeCard(
      this._exchangeOldCard.cardNr,
      newCard.cardNr,
      this._exchangeWithFee
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._exchangeOldCard = null;
          this.cardCodeInputModalComponent.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this._notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`exchange-card/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
